import React, { useEffect, useState, useCallback } from 'react';
import { Trophy, DollarSign, Package, TrendingUp, Percent, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { getRentabilidadComparativa } from '../../lib/api';
import { toast } from 'sonner';

const fmt = (n) => Number(n || 0).toLocaleString('en-US');
const fmtMoney = (n) =>
  `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Comparativa de rentabilidad por lote:
 * - Un card por lote (activo)
 * - KPIs: producido, vendido, ingresos, costos, utilidad, ROI
 * - Ranking automático (mayor ROI arriba)
 */
export const RentabilidadPorLote = () => {
  const [lotes, setLotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getRentabilidadComparativa();
      const list = res.data?.lotes || [];
      // Ordenar por ROI desc
      list.sort((a, b) => (Number(b.roi_pct) || 0) - (Number(a.roi_pct) || 0));
      setLotes(list);
    } catch (err) {
      toast.error('No se pudo cargar la comparativa de rentabilidad');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
              <Trophy className="w-5 h-5" /> Rentabilidad por Lote
            </CardTitle>
            <CardDescription className="mt-1">
              Utilidad, ROI e ingresos reales por lote — basado en el reparto FIFO de facturas.
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={cargar} disabled={loading} data-testid="btn-refresh-rent-lote">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading && lotes.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">Cargando…</div>
        ) : lotes.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground">
            <Trophy className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>Aún no hay lotes activos con datos de rentabilidad.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {lotes.map((L, idx) => {
              const lote = L.lote || {};
              const roi = Number(L.roi_pct) || 0;
              const utilidad = Number(L.utilidad) || 0;
              const utilPositiva = utilidad >= 0;
              return (
                <div
                  key={lote.id}
                  className="border border-border rounded-lg overflow-hidden bg-card"
                  data-testid={`card-rentab-lote-${lote.id}`}
                >
                  <div className="px-4 py-3 border-b border-border bg-muted/40">
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <div className="flex items-center gap-2">
                          {idx === 0 && (
                            <Badge className="gap-0.5 text-[10px]">
                              <Trophy className="w-2.5 h-2.5" /> Top
                            </Badge>
                          )}
                          <span className="font-semibold text-sm" style={{ fontFamily: 'Space Grotesk' }}>
                            {(() => {
                              const code = String(lote.numero_lote || lote.codigo || lote.nombre || '').trim();
                              if (!code) return `Lote ${(lote.id || '').slice(0, 6)}`;
                              return /^lote\b/i.test(code) ? code : `Lote ${code}`;
                            })()}
                          </span>
                        </div>
                        <div className="text-[10px] text-muted-foreground mt-0.5">
                          {L.naves_count} nave{L.naves_count === 1 ? '' : 's'} ·{' '}
                          {fmt(L.gallinas_actuales)} gallinas
                        </div>
                      </div>
                      <div className="text-right">
                        <div
                          className={`text-lg tabular-nums font-bold ${
                            roi >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'
                          }`}
                          data-testid={`roi-${lote.id}`}
                        >
                          {roi.toFixed(1)}%
                        </div>
                        <div className="text-[9px] uppercase tracking-wider text-muted-foreground">ROI</div>
                      </div>
                    </div>
                  </div>
                  <div className="p-3 grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-muted/30 rounded p-2">
                      <div className="text-[10px] text-muted-foreground flex items-center gap-1">
                        <Package className="w-3 h-3" /> Producido
                      </div>
                      <div className="tabular-nums font-semibold mt-0.5">{fmt(L.total_producido)}</div>
                    </div>
                    <div className="bg-muted/30 rounded p-2">
                      <div className="text-[10px] text-muted-foreground flex items-center gap-1">
                        <TrendingUp className="w-3 h-3" /> Vendido
                      </div>
                      <div className="tabular-nums font-semibold mt-0.5">{fmt(L.total_vendido)}</div>
                    </div>
                    <div className="bg-emerald-50 dark:bg-emerald-950/20 rounded p-2 border border-emerald-200/40 dark:border-emerald-900/30">
                      <div className="text-[10px] text-emerald-800 dark:text-emerald-400 flex items-center gap-1">
                        <DollarSign className="w-3 h-3" /> Ingresos
                      </div>
                      <div className="tabular-nums font-semibold mt-0.5 text-emerald-900 dark:text-emerald-100">
                        {fmtMoney(L.total_ingresos)}
                      </div>
                    </div>
                    <div className="bg-red-50 dark:bg-red-950/20 rounded p-2 border border-red-200/40 dark:border-red-900/30">
                      <div className="text-[10px] text-red-800 dark:text-red-400 flex items-center gap-1">
                        <DollarSign className="w-3 h-3" /> Costos
                      </div>
                      <div className="tabular-nums font-semibold mt-0.5 text-red-900 dark:text-red-100">
                        {fmtMoney(L.total_costos)}
                      </div>
                    </div>
                    <div
                      className={`col-span-2 rounded p-2 border ${
                        utilPositiva
                          ? 'bg-primary/5 border-primary/30'
                          : 'bg-red-100/50 dark:bg-red-950/40 border-red-300/60 dark:border-red-800/50'
                      }`}
                    >
                      <div className="text-[10px] text-muted-foreground flex items-center gap-1">
                        <Percent className="w-3 h-3" /> Utilidad
                      </div>
                      <div
                        className={`tabular-nums font-bold text-sm mt-0.5 ${
                          utilPositiva ? 'text-primary' : 'text-red-700 dark:text-red-400'
                        }`}
                        data-testid={`utilidad-${lote.id}`}
                      >
                        {utilPositiva ? '+' : ''}
                        {fmtMoney(utilidad)}
                      </div>
                    </div>
                    <div className="col-span-2 text-[10px] text-muted-foreground pt-1 border-t border-border/40 mt-1 flex justify-between">
                      <span>Costo/huevo: {fmtMoney(L.costo_por_huevo)}</span>
                      <span>Ingreso/huevo vendido: {fmtMoney(L.ingreso_por_huevo)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RentabilidadPorLote;
